import type { ReactNode } from 'react';
import type { OwnerBannerOption } from '@clarity/contract';
import { useI18n } from '../../lib/i18n.js';
import { dayMonth, dec } from '../format.js';
import {
  dateTileParts,
  daysUntil,
  feeSummary,
  runLengthDays,
  sameRun,
  todayUtc,
} from './bannerFormat.js';

/**
 * What a row renders: an owner banner option as the server returns it. The
 * admin editor's "Owners will see" preview builds the same shape client-side
 * from its draft form, so only the fields the row reads are required.
 */
export type BannerRowOption = Pick<
  OwnerBannerOption,
  | 'id'
  | 'platformKey'
  | 'name'
  | 'arabicName'
  | 'where'
  | 'notes'
  | 'askBy'
  | 'runs'
  | 'openRuns'
  | 'nextRun'
  | 'currentRun'
  | 'isNew'
>;

/**
 * The How-to-join panel, driven by the owner tab: it owns the open state, the
 * clipboard write and the "Copied" flag. The row only renders them.
 */
export interface BannerJoinProps {
  open: boolean;
  onToggle: () => void;
  /** The Join_Message text (see `joinMessage`). */
  message: string;
  onCopy: () => void;
  /** Show the "Copied" confirmation. */
  copied: boolean;
}

export interface BannerOptionRowProps {
  option: BannerRowOption;
  /** Today's UTC calendar day, for the Ask-by tone. Defaults to the clock; tests pin it. */
  today?: string;
  /** Show the platform chip next to the title (the admin preview does). */
  showPlatform?: boolean;
  /** Omit for the preview: no How-to-join button or panel. */
  join?: BannerJoinProps;
}

/** Ask-by within this many days reads in the warning tone (mock `ownerBadges`). */
const ASK_BY_SOON_DAYS = 21;

/**
 * One banner option, shared by the owner "Banners you can join" tab and the
 * admin editor's live preview (clarity-banner-options Req 6.4, 7.1, 7.2).
 * Reproduces the `optionRow` markup in `brand/banner-options-mock.html`: date
 * tile (next run), title + Arabic name, where, notes, open runs with the
 * current one marked "Running now", fee head, New and Ask-by badges, and the
 * optional How-to-join panel. Presentational only.
 */
export function BannerOptionRow({ option, today, showPlatform = false, join }: BannerOptionRowProps) {
  const { t, localeTag } = useI18n();
  const day = today ?? todayUtc();
  const platform = t(`bannerOptions.platform.${option.platformKey}`);
  const tile = dateTileParts(option.nextRun.from, localeTag);
  const fee = feeSummary(option);
  const askByLeft = option.askBy ? daysUntil(day, option.askBy) : null;
  const panelId = `banner-join-${option.id}`;

  return (
    <li className="banner-row">
      <div className="banner-row__when" aria-hidden="true">
        <span className="banner-row__when-m">{tile.month}</span>
        <span className="banner-row__when-d">{tile.day}</span>
      </div>

      <div className="banner-row__body">
        <div className="banner-row__title">
          <h3>{option.name || t('banners.untitled')}</h3>
          {option.arabicName && (
            <bdi className="banner-row__ar" dir="rtl" lang="ar">
              {option.arabicName}
            </bdi>
          )}
          {showPlatform && (
            <span className={`banner-row__plat banner-row__plat--${option.platformKey}`}>{platform}</span>
          )}
        </div>
        <div className="banner-row__where">
          <PinIcon />
          <span>{option.where || t('banners.where.placeholder')}</span>
        </div>
        {option.notes && <p className="banner-row__notes">{option.notes}</p>}
        <ul className="banner-row__runs">
          {option.openRuns.map((run) => {
            const days = runLengthDays(run);
            return (
              <li key={`${run.from}:${run.to}`}>
                <span className="banner-row__run-dates">
                  {t('banners.run.dates', { from: dayMonth(run.from, localeTag), to: dayMonth(run.to, localeTag) })}
                </span>
                <span className="banner-row__run-len">
                  {t(days === 1 ? 'banners.run.days.one' : 'banners.run.days.many', { count: days })}
                </span>
                <span className="banner-row__run-fee">{t('banners.run.fee', { fee: dec(run.fee, 2, localeTag) })}</span>
                {sameRun(run, option.currentRun) && (
                  <span className="banner-row__badge banner-row__badge--live">
                    <i aria-hidden="true" />
                    {t('banners.run.runningNow')}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <div className="banner-row__act">
        <div className="banner-row__fee">
          <div className="banner-row__fee-v">
            {fee.min === fee.max
              ? dec(fee.min, 2, localeTag)
              : t('banners.fee.range', { min: dec(fee.min, 2, localeTag), max: dec(fee.max, 2, localeTag) })}
            <small>{t('banners.fee.currency')}</small>
          </div>
          <div className="banner-row__fee-l">{t(fee.perRun ? 'banners.fee.perRun' : 'banners.fee.forTheRun')}</div>
        </div>
        <div className="banner-row__badges">
          {option.isNew && (
            <span className="banner-row__badge banner-row__badge--new">
              <i aria-hidden="true" />
              {t('banners.badge.new')}
            </span>
          )}
          {option.askBy && askByLeft !== null && askByLeft >= 0 && (
            <span
              className={`banner-row__badge ${askByLeft <= ASK_BY_SOON_DAYS ? 'banner-row__badge--soon' : 'banner-row__badge--quiet'}`}
            >
              {askByLeft <= ASK_BY_SOON_DAYS && <i aria-hidden="true" />}
              {t('banners.badge.askBy', { date: dayMonth(option.askBy, localeTag) })}
            </span>
          )}
        </div>
        {join && (
          <button
            type="button"
            className={`banner-row__join-btn ${join.open ? 'ghost' : 'primary'}`}
            aria-expanded={join.open}
            aria-controls={panelId}
            onClick={join.onToggle}
          >
            <MsgIcon />
            {join.open ? t('banners.join.hide') : t('banners.join.open')}
          </button>
        )}
      </div>

      {join && (
        <div className="banner-row__join" id={panelId} data-open={join.open} inert={!join.open}>
          <div>
            <div className="banner-row__join-in">
              <p>
                {t('banners.join.lead.before')} <b>{t('banners.join.lead.manager', { platform })}</b>{' '}
                {t('banners.join.lead.after')}
              </p>
              <pre className="banner-row__msg">{join.message}</pre>
              <div className="banner-row__join-row">
                <button type="button" className="ghost banner-row__copy" onClick={join.onCopy}>
                  <CopyIcon />
                  {t('banners.join.copy')}
                </button>
                <span className="banner-row__copied" role="status">
                  {join.copied && (
                    <>
                      <CheckIcon />
                      {t('banners.join.copied')}
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}

// ── Icons (the mock's stroke set) ──────────────────────────────────────────────

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      className="banner-row__i"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function PinIcon() {
  return (
    <Icon>
      <path d="M12 21s-6-5.5-6-11a6 6 0 1 1 12 0c0 5.5-6 11-6 11z" />
      <circle cx="12" cy="10" r="2" />
    </Icon>
  );
}

function MsgIcon() {
  return (
    <Icon>
      <path d="M4 5h16v11H8l-4 4z" />
    </Icon>
  );
}

function CopyIcon() {
  return (
    <Icon>
      <rect x="8" y="8" width="12" height="12" rx="2" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </Icon>
  );
}

function CheckIcon() {
  return (
    <Icon>
      <path d="m5 12 5 5 9-10" />
    </Icon>
  );
}
